import { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  CheckCircle,
  Clock,
  Timer,
  ChatText,
  ArrowsLeftRight,
  Calendar,
} from '@phosphor-icons/react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import api from '../services/api'

interface MyMetricsData {
  completedChats: number
  currentlyAssigned: number
  avgFirstResponseMinutes: number | null
  avgResolutionMinutes: number | null
  messagesSent: number
  chatsByDay: { date: string; count: number }[]
  transfers: { received: number; made: number }
}

function StatCard({
  label,
  value,
  icon: Icon,
  helpText,
  color = 'yellow.500',
}: {
  label: string
  value: string | number
  icon: React.ElementType
  helpText?: string
  color?: string
}) {
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  return (
    <Box bg={surfaceBg} p={6} borderRadius="xl">
      <HStack justify="space-between">
        <Stat>
          <StatLabel color={textMuted}>{label}</StatLabel>
          <StatNumber color={textPrimary} fontSize="3xl">
            {value}
          </StatNumber>
          {helpText && <StatHelpText color={textMuted}>{helpText}</StatHelpText>}
        </Stat>
        <Box
          p={3}
          bg="whiteAlpha.100"
          borderRadius="lg"
        >
          <Icon size={28} color={color} />
        </Box>
      </HStack>
    </Box>
  )
}

function formatMinutes(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '-'
  if (minutes < 60) return `${Math.round(minutes)}min`
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  if (hours < 24) return `${hours}h ${mins}min`
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24
  return `${days}d ${remainingHours}h`
}

export default function MyMetrics() {
  const [metrics, setMetrics] = useState<MyMetricsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [period, setPeriod] = useState('30')
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  useEffect(() => {
    const fetchMetrics = async () => {
      setIsLoading(true)
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - parseInt(period))

      try {
        const res = await api.get('/my-metrics', {
          params: { startDate: startDate.toISOString() },
        })
        setMetrics(res.data)
      } catch (error) {
        console.error('Error fetching metrics:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchMetrics()
  }, [period])

  if (isLoading) {
    return (
      <Box p={6} h="100%" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="yellow.500" />
      </Box>
    )
  }

  return (
    <Box p={6} h="100%" overflowY="auto">
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <Text fontSize="2xl" fontWeight="bold" color={textPrimary}>
            Minhas Métricas
          </Text>
          <Select
            w="200px"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            bg={surfaceBg}
          >
            <option value="7">Últimos 7 dias</option>
            <option value="15">Últimos 15 dias</option>
            <option value="30">Últimos 30 dias</option>
            <option value="60">Últimos 60 dias</option>
            <option value="90">Últimos 90 dias</option>
          </Select>
        </HStack>

        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          <StatCard
            label="Atendimentos Concluídos"
            value={metrics?.completedChats || 0}
            icon={CheckCircle}
            color="#48BB78"
          />
          <StatCard
            label="Em Andamento"
            value={metrics?.currentlyAssigned || 0}
            icon={Clock}
            color="#4299E1"
          />
          <StatCard
            label="Mensagens Enviadas"
            value={metrics?.messagesSent || 0}
            icon={ChatText}
            color="#9F7AEA"
          />
          <StatCard
            label="Tempo Médio 1ª Resposta"
            value={formatMinutes(metrics?.avgFirstResponseMinutes || null)}
            icon={Timer}
            color="#ECC94B"
          />
          <StatCard
            label="Tempo Médio Resolução"
            value={formatMinutes(metrics?.avgResolutionMinutes || null)}
            icon={Calendar}
            color="#ED8936"
          />
          <StatCard
            label="Transferências"
            value={`${metrics?.transfers.received || 0} / ${metrics?.transfers.made || 0}`}
            icon={ArrowsLeftRight}
            helpText="Recebidas / Feitas"
            color="#F56565"
          />
        </SimpleGrid>

        {/* Daily Performance */}
        <Box bg={surfaceBg} borderRadius="xl" overflow="hidden">
          <Box p={4} borderBottom="1px" borderColor={borderColor}>
            <Text fontWeight="semibold" color={textPrimary}>
              Desempenho Diário
            </Text>
          </Box>
          <Box overflowX="auto">
            <Table>
              <Thead>
                <Tr>
                  <Th color={textMuted}>Data</Th>
                  <Th color={textMuted} isNumeric>Atendimentos Concluídos</Th>
                </Tr>
              </Thead>
              <Tbody>
                {metrics?.chatsByDay.length === 0 ? (
                  <Tr>
                    <Td colSpan={2}>
                      <Text color={textMuted} textAlign="center" py={4}>
                        Nenhum atendimento concluído no período
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  metrics?.chatsByDay.map((day) => (
                    <Tr key={day.date}>
                      <Td>
                        <Text color={textPrimary}>
                          {format(new Date(day.date), "EEEE, dd 'de' MMMM", {
                            locale: ptBR,
                          })}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Badge
                          colorScheme={day.count > 5 ? 'green' : day.count > 2 ? 'yellow' : 'gray'}
                          fontSize="md"
                          px={3}
                          py={1}
                        >
                          {day.count}
                        </Badge>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </VStack>
    </Box>
  )
}
